import { useEffect } from "react";

const SITE_URL = "https://accommodationsuppliers.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/brand/og-social-share.png`;
const DEFAULT_TITLE = "Accommodation Suppliers — B2B hotel & hospitality directory";
const DEFAULT_DESCRIPTION =
  "The B2B directory for accommodation suppliers — connecting hotels, resorts and hospitality buyers with verified linen, furniture, amenities and services providers.";

// Upsert exactly ONE tag per selector (removes duplicates), so crawlers never see conflicting metadata.
function setTag(selector, create, attrs) {
  const nodes = [...document.head.querySelectorAll(selector)];
  const node = nodes.shift() || document.head.appendChild(create());
  nodes.forEach((n) => n.remove());
  Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, v));
  return node;
}
const meta = (attr, key, content) => setTag(`meta[${attr}="${key}"]`, () => document.createElement("meta"), { [attr]: key, content });

export function applySeo({ title, description, image, path, type, noindex }) {
  // Titles edited in Admin → SEO may already carry the site name; never append the suffix twice.
  const fullTitle = title ? (/accommodation suppliers/i.test(title) ? title : `${title} · Accommodation Suppliers`) : DEFAULT_TITLE;
  const url = `${SITE_URL}${path || "/"}`;
  document.title = fullTitle;
  meta("name", "description", description);
  setTag('link[rel="canonical"]', () => document.createElement("link"), { rel: "canonical", href: url });
  meta("property", "og:type", type);
  meta("property", "og:site_name", "Accommodation Suppliers");
  meta("property", "og:title", fullTitle);
  meta("property", "og:description", description);
  meta("property", "og:url", url);
  meta("property", "og:image", image);
  meta("property", "og:image:width", "1200");
  meta("property", "og:image:height", "630");
  meta("name", "twitter:card", "summary_large_image");
  meta("name", "twitter:title", fullTitle);
  meta("name", "twitter:description", description);
  meta("name", "twitter:image", image);
  const robots = document.head.querySelector('meta[name="robots"]');
  if (noindex) meta("name", "robots", "noindex, nofollow");
  else if (robots) robots.remove();
}

const DEFAULTS = { title: "", description: DEFAULT_DESCRIPTION, image: DEFAULT_OG_IMAGE, path: "", type: "website", noindex: false };

// Per-page SEO + Open Graph + Twitter cards. Pages should pass at least a title.
// On unmount the defaults are restored, then the next page's SEO (if any) applies on top.
export default function SEO({
  title = "",
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_OG_IMAGE,
  path = "",
  type = "website",
  noindex = false,
}) {
  useEffect(() => {
    applySeo({ title, description, image, path, type, noindex });
    return () => applySeo(DEFAULTS);
  }, [title, description, image, path, type, noindex]);
  return null;
}
